import {WithdrawalMinimum} from "../../components/WithdrawalMinimum";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  getMerchantOverview,
  listMerchantBalanceMovements,
} from "../../api";
import type {
  MerchantMovementItem,
  MerchantMovementsResponse,
  MerchantOverviewResponse,
} from "../../api";
import {useAuth} from "../../auth";
import {
  MerchantMovementsList,
  MerchantSettlementSummary,
} from "../../components";
import {useMerchantQuery} from "../../hooks";
import {formatMerchantDate} from "../../utils";
import {
  createMerchantWithdrawal,
  listMerchantWithdrawals,
} from "../../api/merchantWithdrawals";
import type {MerchantWithdrawal, MerchantWithdrawalAvailability, MerchantWithdrawalNetwork} from "../../api/merchantWithdrawals";
import {ApiError} from "../../api/client";
import {apiErrorCode} from "../../api/error-codes";
import {newClientRequestId} from "./MerchantCreateOrderPage";

const MOVEMENTS_PAGE_SIZE = 50;

interface BalancePageResponse {
  key: string;
  overview: MerchantOverviewResponse;
  movements: MerchantMovementsResponse;
}

export function MerchantBalancePage(): JSX.Element {
  const {token} = useAuth();
  const [overview, setOverview] = useState<MerchantOverviewResponse | null>(null);
  const [movements, setMovements] = useState<MerchantMovementItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [withdrawals, setWithdrawals] = useState<MerchantWithdrawal[]>([]);
  const [availability, setAvailability] = useState<MerchantWithdrawalAvailability | null>(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalAddress, setWithdrawalAddress] = useState("");
  const [withdrawalNetwork, setWithdrawalNetwork] = useState<MerchantWithdrawalNetwork>("trc20");
  const [withdrawalBusy, setWithdrawalBusy] = useState(false);
  const [withdrawalMessage, setWithdrawalMessage] = useState<string | null>(null);
  const [pendingCreate, setPendingCreate] = useState<{id: string; amount: string; address: string; network: MerchantWithdrawalNetwork} | null>(null);
  const [createUncertain, setCreateUncertain] = useState(false);

  const requestKey = useMemo(
    () => `offset=${offset}|limit=${MOVEMENTS_PAGE_SIZE}|reload=${reloadKey}`,
    [offset, reloadKey],
  );
  const request = useCallback(
    async (signal: AbortSignal): Promise<BalancePageResponse> => {
      const [overviewResponse, movementsResponse] = await Promise.all([
        getMerchantOverview(token, signal),
        listMerchantBalanceMovements(token, {
          limit: MOVEMENTS_PAGE_SIZE,
          offset,
        }, signal),
      ]);
      return {
        key: requestKey,
        overview: overviewResponse,
        movements: movementsResponse,
      };
    },
    [offset, requestKey, token],
  );
  const query = useMerchantQuery(request, `merchant-balance:${requestKey}`, {
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (!query.data || query.data.key !== requestKey) return;
    setOverview(query.data.overview);
    setMovements((current) => (
      offset === 0
        ? query.data!.movements.items
        : mergeMovements(current, query.data!.movements.items)
    ));
    setHasMore(query.data.movements.hasMore);
    setNextOffset(query.data.movements.nextOffset ?? null);
  }, [offset, query.data, requestKey]);

  const reloadFirstPage = (): void => {
    setOverview(null);
    setMovements([]);
    setOffset(0);
    setHasMore(false);
    setNextOffset(null);
    setReloadKey((value) => value + 1);
  };

  const loadMore = (): void => {
    if (!hasMore || query.loading || query.refreshing) return;
    setOffset(nextOffset ?? offset + MOVEMENTS_PAGE_SIZE);
  };

  const hasContent = overview !== null || movements.length > 0;
  const busy = query.loading || query.refreshing;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void listMerchantWithdrawals(token).then((response) => {
      if (!cancelled) { setWithdrawals(response.items); setAvailability(response.availability); }
    }).catch(() => { if (!cancelled) setWithdrawalMessage("Не удалось загрузить выводы USDT."); });
    return () => { cancelled = true; };
  }, [reloadKey, token]);

  const networks = useMemo(() => availability?.networks ?? [{network: "trc20" as const, feePolicy: "gasfree_included" as const}], [availability]);
  const networkAvailable = networks.some((item) => item.network === withdrawalNetwork);
  useEffect(() => {
    if (!withdrawalBusy && !createUncertain && !networkAvailable && networks.length > 0) {
      setWithdrawalNetwork(networks[0].network);
      setWithdrawalAddress("");
    }
  }, [withdrawalBusy, createUncertain, networkAvailable, networks]);

  const submitWithdrawal = async (): Promise<void> => {
    if (withdrawalBusy) return;
    const amount = withdrawalAmount.trim();
    const address = withdrawalAddress.trim();
    if ((!createUncertain && (!availability?.eligible || availability.available === null || !networkAvailable)) || !isPositiveDecimal(amount)) {
      setWithdrawalMessage(availability?.reason || "Укажите положительную сумму USDT в доступном остатке.");
      return;
    }
    if (!address) { setWithdrawalMessage(`Укажите адрес ${withdrawalNetwork.toUpperCase()} для получения.`); return; }
    if (withdrawalNetwork === "bep20" && (!/^0x[0-9a-fA-F]{40}$/.test(address) || /^0x0{40}$/.test(address))) {
      setWithdrawalMessage("Укажите адрес BEP20: 0x и 40 шестнадцатеричных символов."); return;
    }
    setWithdrawalBusy(true); setWithdrawalMessage(null);
    try {
      const clientRequestId = pendingCreate && pendingCreate.amount === amount && pendingCreate.address === address && pendingCreate.network === withdrawalNetwork
        ? pendingCreate.id
        : newClientRequestId();
      setPendingCreate({id: clientRequestId, amount, address, network: withdrawalNetwork});
      const created = await createMerchantWithdrawal(token, {clientRequestId, amount, address, network: withdrawalNetwork});
      setWithdrawals((items) => [created, ...items.filter((item) => item.id !== created.id)]);
      setWithdrawalAmount(""); setWithdrawalAddress("");
      setPendingCreate(null);
      setCreateUncertain(false);
      setWithdrawalMessage("Заявка создана. Сумма удержана из баланса.");
      setReloadKey((value) => value + 1);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && apiErrorCode(error) === "withdrawal_below_minimum") {
        setWithdrawalMessage("Сумма ниже действующего минимума USDT. Обновите минимум и увеличьте сумму."); setPendingCreate(null); setCreateUncertain(false); return;
      }
      // The server validates the address before resolving the request ID, so this refusal is final for the same body.
      if (error instanceof ApiError && error.status === 400 && apiErrorCode(error) === "invalid_bep20_address_checksum") {
        setWithdrawalMessage("Контрольная сумма адреса BEP20 неверна. Скопируйте адрес заново из кошелька получателя, сохранив регистр букв."); setPendingCreate(null); setCreateUncertain(false); return;
      }
      if (createUncertain || isUncertainCreateError(error)) {
        setCreateUncertain(true);
        setWithdrawalMessage("Ответ не получен. Нажмите «Проверить создание»: повторный запрос не создаст второй вывод.");
        try { const latest = await listMerchantWithdrawals(token); setWithdrawals(latest.items); setAvailability(latest.availability); } catch { /* keep the retry UUID */ }
      } else {
        setPendingCreate(null);
        setCreateUncertain(false);
        setWithdrawalMessage("Не удалось создать заявку вывода. Проверьте данные и попробуйте ещё раз.");
      }
    }
    finally { setWithdrawalBusy(false); }
  };

  return (
    <div className="page merchant-page merchant-balance-page">
      <header className="page-header merchant-page-header">
        <div>
          <h1>Баланс</h1>
          <p className="page-subtitle">
            Текущее состояние и append-only история движений
          </p>
        </div>
        <div className="merchant-refresh-block">
          <span className="merchant-updated-at">
            {query.lastSuccessfulAt
              ? `Обновлено ${formatMerchantDate(query.lastSuccessfulAt)}`
              : "Данные ещё не загружены"}
          </span>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={reloadFirstPage}
            disabled={busy}
          >
            {busy ? "Обновление…" : "Обновить"}
          </button>
        </div>
      </header>

      {query.error && hasContent && (
        <div className="alert alert-warning merchant-stale-banner" role="status">
          {query.error} Показаны уже загруженные данные.
          <button className="btn-link-clear" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </div>
      )}

      {query.loading && !hasContent && (
        <div className="merchant-skeleton" aria-label="Загрузка баланса" aria-busy="true">
          <div className="merchant-skeleton-grid">
            <span/>
            <span/>
            <span/>
          </div>
          <div className="merchant-skeleton-panel"/>
        </div>
      )}

      {!query.loading && !hasContent && query.error && (
        <section className="merchant-error-state" role="alert">
          <h2>Не удалось загрузить баланс</h2>
          <p>{query.error}</p>
          <button className="btn btn-primary" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </section>
      )}

      {overview && (
        <>
          <MerchantSettlementSummary overview={overview}/>
          <section className="merchant-section-card">
            <div className="section-header merchant-section-header">
              <div>
                <h2>Движения баланса</h2>
                <span>Загружено: {movements.length}</span>
              </div>
            </div>
            <MerchantMovementsList
              items={movements}
              emptyMessage="Движений по балансу пока нет."
            />
            {hasMore && (
              <div className="merchant-load-more">
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={loadMore}
                  disabled={busy}
                >
                  {busy ? "Загрузка…" : "Показать ещё"}
                </button>
              </div>
            )}
          </section>
          <section className="merchant-section-card merchant-withdrawal-section">
            <div className="section-header merchant-section-header"><div><h2>Вывод USDT</h2><span>Сеть и адрес получения указываются для каждой заявки</span></div></div>
            <p>{withdrawalNetwork === "bep20" ? "Получатель получит всю указанную сумму USDT в BNB Smart Chain (BEP20). Газ BNB оплачивается отправителем отдельно." : "Получатель получит всю указанную сумму USDT в сети TRON (TRC20). Комиссию сети оплачиваем мы."}</p>
            {availability && <div className="merchant-summary-grid">
              <div><span>Баланс</span><strong className="mono">{availability.balance} USDT</strong></div>
              <div><span>В обработке</span><strong className="mono">{availability.reserved} USDT</strong></div>
              <div><span>Доступно</span><strong className="mono">{availability.available === null ? "Недоступно" : `${availability.available} USDT`}</strong></div>
            </div>}
            {availability?.reason && <div className="alert alert-warning" role="status">{availability.reason}</div>}
            <div className="form-grid">
              <label>Сеть вывода<select value={withdrawalNetwork} onChange={(event) => { setWithdrawalNetwork(event.target.value as MerchantWithdrawalNetwork); setWithdrawalAddress(""); setPendingCreate(null); }} disabled={withdrawalBusy || createUncertain || !availability?.eligible}>
                {(createUncertain && !networkAvailable ? [...networks, {network: withdrawalNetwork, feePolicy: "gasfree_included" as const}] : networks).map((item) => <option key={item.network} value={item.network}>{item.network === "bep20" ? "BEP20 (BNB Smart Chain)" : "TRC20 (TRON)"}</option>)}
              </select></label>
              <WithdrawalMinimum audience="merchant"/>
              <label>Сумма USDT<input type="text" inputMode="decimal" value={withdrawalAmount} onChange={(event) => setWithdrawalAmount(event.target.value)} disabled={withdrawalBusy || createUncertain || !availability?.eligible}/></label>
              <label>Адрес {withdrawalNetwork.toUpperCase()}<input type="text" value={withdrawalAddress} onChange={(event) => setWithdrawalAddress(event.target.value)} disabled={withdrawalBusy || createUncertain || !availability?.eligible}/></label>
            </div>
            <button className="btn btn-primary" type="button" onClick={() => void submitWithdrawal()} disabled={withdrawalBusy || (!createUncertain && (!availability?.eligible || availability.available === null || !networkAvailable))}>{withdrawalBusy ? "Создание…" : createUncertain ? "Проверить создание" : "Запросить вывод"}</button>
            {withdrawalMessage && <div className="alert" role="status">{withdrawalMessage}</div>}
          </section>
          <section className="merchant-section-card">
            <div className="section-header merchant-section-header"><div><h2>История выводов</h2><span>{withdrawals.length} заявок</span></div></div>
            {withdrawals.length === 0 ? <p>Выводов пока нет.</p> : <div className="table-wrapper merchant-withdrawals-table-wrap"><table className="merchant-withdrawals-table"><thead><tr><th>Дата</th><th>Сумма</th><th>Получено</th><th>Комиссия</th><th>Адрес / tx</th><th>Статус</th></tr></thead><tbody>{withdrawals.map((item) => <tr key={item.id}><td data-label="Дата">{formatMerchantDate(item.createdAt)}</td><td className="mono" data-label="Сумма">{item.amount} USDT</td><td className="mono" data-label="Получено">{item.received === null ? "—" : `${item.received} USDT`}</td><td className="mono" data-label="Комиссия">{item.fee === null ? "—" : `${item.fee} USDT`}</td><td data-label="Адрес / tx"><strong>{item.network.toUpperCase()}</strong><br/><code className="mono" title={item.address}>{item.address}</code> <button className="btn-link-clear" type="button" onClick={() => void copyText(item.address)}>Копировать</button>{item.txHash && <><br/><code className="mono" title={item.txHash}>{item.txHash}</code></>}{item.verificationReason && <><br/><span>{item.verificationReason}</span></>}</td><td data-label="Статус">{withdrawalStatusLabel(item.status)}{item.rejectionReason ? `: ${item.rejectionReason}` : ""}</td></tr>)}</tbody></table></div>}
          </section>
        </>
      )}
    </div>
  );
}

export function isPositiveDecimal(value: string): boolean { return /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/.test(value) && /[1-9]/.test(value); }
function withdrawalStatusLabel(status: MerchantWithdrawal["status"]): string { return ({requested: "Запрошен", accepted: "Принят", sending: "Отправляется", completed: "Завершён", rejected: "Отклонён"})[status]; }
function isUncertainCreateError(error: unknown): boolean { return !(error instanceof ApiError) || error.status === 0 || error.status >= 500; }
async function copyText(value: string): Promise<void> { if (navigator.clipboard) await navigator.clipboard.writeText(value); }

function mergeMovements(
  current: MerchantMovementItem[],
  next: MerchantMovementItem[],
): MerchantMovementItem[] {
  const byId = new Map(current.map((item) => [item.id, item]));
  for (const item of next) byId.set(item.id, item);
  return Array.from(byId.values());
}
